import { cldConfig } from '../config/cloudinary';
import { enrichCloudinaryFileMeta } from './cloudinaryDelivery';

const CLOUD_NAME = cldConfig.cloudName || 'dkyoxq3cc';
const MAX_THESIS_MB = 15;
const MAX_SUPPORTING_MB = 10;
const MAX_DATASET_MB = 25;

const ALLOWED_PDF = ['application/pdf'];
const ALLOWED_DOCS = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
];
const ALLOWED_DATASET = [
  ...ALLOWED_DOCS,
  'text/csv',
  'application/zip',
  'application/x-zip-compressed',
];

function mbToBytes(mb) {
  return mb * 1024 * 1024;
}

async function uploadToCloudinary(file, { folder, publicIdPrefix, asPdf = false }) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', cldConfig.uploadPreset || 'ml_default');
  formData.append('folder', folder);
  formData.append('public_id', `${publicIdPrefix}_${Date.now()}`);

  let endpoint;
  if (asPdf) {
    // PDF as image resource enables inline viewing + pg_1 previews in admin
    endpoint = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;
  } else if (file.type.startsWith('image/')) {
    endpoint = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;
  } else {
    endpoint = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/raw/upload`;
    formData.append('resource_type', 'raw');
  }

  const response = await fetch(endpoint, { method: 'POST', body: formData });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error?.message || 'Upload failed');
  }

  const base = {
    url: data.secure_url,
    publicId: data.public_id,
    originalName: file.name,
    resourceType: asPdf || file.type.startsWith('image/') ? 'image' : 'raw',
  };
  if (data.bytes != null) base.bytes = data.bytes;
  if (data.format) base.format = data.format;
  if (asPdf) base.format = 'pdf';

  return enrichCloudinaryFileMeta(base, { isPdf: asPdf });
}

export function validateResearchFile(file, type) {
  if (!file) return { valid: false, error: 'No file selected' };

  const rules = {
    thesisPdf: {
      types: ALLOWED_PDF,
      maxBytes: mbToBytes(MAX_THESIS_MB),
      label: 'Thesis PDF',
    },
    supportingDocs: {
      types: ALLOWED_DOCS,
      maxBytes: mbToBytes(MAX_SUPPORTING_MB),
      label: 'Supporting document',
    },
    dataset: {
      types: ALLOWED_DATASET,
      maxBytes: mbToBytes(MAX_DATASET_MB),
      label: 'Dataset',
    },
  };

  const rule = rules[type];
  if (!rule) return { valid: false, error: 'Invalid upload type' };

  if (!rule.types.includes(file.type)) {
    return { valid: false, error: `${rule.label} must be a supported file type` };
  }
  if (file.size > rule.maxBytes) {
    return {
      valid: false,
      error: `${rule.label} must be under ${rule.maxBytes / (1024 * 1024)}MB`,
    };
  }
  return { valid: true };
}

export async function uploadResearchFile(file, userId, fileKey) {
  const validation = validateResearchFile(file, fileKey);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const asPdf = file.type === 'application/pdf';
  return uploadToCloudinary(file, {
    folder: `research-support/${userId}`,
    publicIdPrefix: fileKey,
    asPdf,
  });
}
